import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { User } from "../schemas/userSchema";
import {
  userService,
  UserFilters,
  UsersResponse,
} from "../api/services/userService";

export const useUsers = (
  filters: UserFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: users,
    isLoading: isUsersLoading,
    refetch,
  } = useQuery<UsersResponse, Error>({
    queryKey: ["users", filters],
    queryFn: () => userService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetUserById = (id?: string) =>
    useQuery<User, Error>({
      queryKey: ["user", id],
      queryFn: () => userService.getById(id!),
      enabled: !!id,
    });

  const createUserMutation = useMutation({
    mutationFn: userService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create user");
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: userService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update user");
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: userService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("User deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete user");
    },
  });

  return {
    users,
    isLoading:
      isUsersLoading ||
      createUserMutation.isPending ||
      updateUserMutation.isPending ||
      deleteUserMutation.isPending,
    useGetUserById,
    refetchUsers: refetch,
    createUser: createUserMutation.mutate,
    updateUser: updateUserMutation.mutate,
    deleteUsers: deleteUserMutation.mutate,
  };
};
